import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMessages } from '@/hooks/useMessages';
import MessageListItem from './MessageListItem';
import Button from '@/components/common/Button';
import Spinner from '@/components/common/Spinner';
import EmptyState from '@/components/common/EmptyState';

export default function MessageList() {
  const [viewMode, setViewMode] = useState<'active' | 'archived'>('active');
  const { data, isLoading, error } = useMessages(viewMode);
  const navigate = useNavigate();

  const isArchiveView = viewMode === 'archived';

  return (
    <div className="flex h-full flex-col">
      {/* Header with toggle and New Message button */}
      <div className="flex items-center justify-between border-b border-neutral-200 p-4">
        <h2 className="text-sm font-semibold text-neutral-700">
          {isArchiveView ? 'Archived Messages' : 'Messages'}
        </h2>
        <div className="flex items-center gap-2">
          {isArchiveView ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('active')}
            >
              Current
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewMode('archived')}
              >
                Archived
              </Button>
              <Button size="sm" onClick={() => navigate('/app/messages/new')}>
                + New
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Message list */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && <Spinner className="py-8" />}

        {error && (
          <div className="p-4 text-center text-sm text-error-600">
            Failed to load messages
          </div>
        )}

        {data && data.messages.length === 0 && (
          <EmptyState
            title={isArchiveView ? 'No archived messages' : 'No messages yet'}
            description={
              isArchiveView
                ? 'Archived conversations will appear here'
                : 'Start a conversation with Jerry'
            }
            action={
              !isArchiveView ? (
                <Button size="sm" onClick={() => navigate('/app/messages/new')}>
                  New Message
                </Button>
              ) : undefined
            }
          />
        )}

        {data?.messages.map((message) => (
          <MessageListItem
            key={message.message_id}
            message={message}
            onClick={() => navigate(`/app/messages/${message.message_id}`)}
          />
        ))}
      </div>
    </div>
  );
}
